import React, { useMemo, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from '@bringg/react-components';
import type { User } from '@bringg/types';

import { TRANSLATION_DOMAINS } from '../../../../consts';
import { FlagIcon } from '../../../../icons/components';
import { useStores } from '../../../../recipes';

import './styles.scss';

interface Props {
	alertOwner: User;
	alertUuid: string;
}

export const ChangeOwnership = observer(({ alertOwner, alertUuid }: Props) => {
	const { t } = useTranslation();

	const {
		usersStore: { currentUser },
		alertsStore: { updateOwnership, getRelatedAlerts }
	} = useStores();

	const [pending, setPending] = useState(false);
	const [ownershipModalShown, setOwnershipModalShown] = useState(false);

	// TODO: discover some ways to memo this data on store or component level
	// "standard" approaches lead to bugs
	const availableAlerts = getRelatedAlerts(alertUuid, alertOwner?.id);

	const updateAlertsOwnership = useCallback(
		async (alertUuids: string[]) => {
			setPending(true);

			const userId = alertOwner ? undefined : currentUser.id;

			await updateOwnership(alertUuids, userId);
			setOwnershipModalShown(false);
			setPending(false);
		},
		[updateOwnership, alertOwner, currentUser.id]
	);

	const openOwnershipModal = useCallback(() => {
		if (availableAlerts.length > 1) {
			setOwnershipModalShown(true);
		} else {
			updateAlertsOwnership([alertUuid]);
		}
	}, [alertUuid, availableAlerts, updateAlertsOwnership]);

	const handleModalOk = useCallback(() => {
		updateAlertsOwnership(availableAlerts.map(alert => alert.uuid));
	}, [availableAlerts, updateAlertsOwnership]);

	const handleModalClose = useCallback(() => {
		setOwnershipModalShown(false);
	}, []);

	const takeOwnershipOverAlert = useCallback(() => {
		updateAlertsOwnership([alertUuid]);
	}, [availableAlerts, updateAlertsOwnership]);

	const ownershipModalFooterButtons = useMemo(() => {
		let buttons = [
			<Button type="link" onClick={handleModalClose} disabled={pending}>
				{t('GLOBAL.CANCEL')}
			</Button>
		];

		if (alertOwner) {
			buttons.push(
				<Button type="primary" className="proceed-ownership" onClick={handleModalOk} disabled={pending}>
					{t('GLOBAL.PROCEED')}
				</Button>
			);
		} else {
			buttons = [
				...buttons,
				<Button type="link" onClick={takeOwnershipOverAlert} disabled={pending}>
					{t('EXCEPTIONS_LIST.TAKE_OWNERSHIP_OVER_ALERT')}
				</Button>,
				<Button type="primary" className="proceed-ownership" onClick={handleModalOk} disabled={pending}>
					{t('EXCEPTIONS_LIST.TAKE_OWNERSHIP_OVER_MULTIPLE_ALERTS', {
						quantity: availableAlerts.length
					})}
				</Button>
			];
		}

		return buttons;
	}, [alertOwner, handleModalOk, takeOwnershipOverAlert]);

	return (
		<div className="alert-owner">
			{alertOwner ? (
				<>
					<Button type="text" className="owner-name">
						<FlagIcon />
						<span className="alert-owner-name">{currentUser.name}</span>
					</Button>
					<Button type="link" className="remove-ownership" disabled={pending} onClick={openOwnershipModal}>
						{t(`${TRANSLATION_DOMAINS.EXCEPTIONS_LIST}.REMOVE_OWNERSHIP`)}
					</Button>
				</>
			) : (
				<Button type="link" className="take-ownership" disabled={pending} onClick={openOwnershipModal}>
					{t(`${TRANSLATION_DOMAINS.EXCEPTIONS_LIST}.TAKE_OWNERSHIP`)}
				</Button>
			)}
			{/* TODO: move modal to separate file(context) as DismissModal */}
			{Boolean(availableAlerts?.length) && (
				<Modal
					className="change-alert-ownership-modal"
					visible={ownershipModalShown}
					closable={false}
					onOk={handleModalOk}
					onCancel={handleModalClose}
					footer={ownershipModalFooterButtons}
					title={
						<>
							<div className="action-indicator" />
							<span>
								{t(
									`${TRANSLATION_DOMAINS.EXCEPTIONS_LIST}.${
										alertOwner ? 'REMOVE_OWNERSHIP' : 'TAKE_OWNERSHIP'
									}`
								)}
							</span>
						</>
					}
				>
					{alertOwner
						? t(`${TRANSLATION_DOMAINS.EXCEPTIONS_LIST}.REMOVE_ALERTS_MODAL_MESSAGE`, {
								taskId: availableAlerts[0].task_id
						  })
						: t(`${TRANSLATION_DOMAINS.EXCEPTIONS_LIST}.OWN_ALERTS_MODAL_MESSAGE`, {
								numberOfAlerts: availableAlerts.length - 1,
								taskId: availableAlerts[0].task_id
						  })}
				</Modal>
			)}
		</div>
	);
});
